import { toast } from "sonner";

/**
 * The one way the app talks back after an action.
 *
 * Standardises the shape already used in `pages/abonnement-detail.tsx`: a
 * success toast says what happened in the past tense ("Abonnement enregistré"),
 * a failure toast says what did NOT happen ("L’abonnement n’a pas été
 * supprimé") and carries the technical reason as its description.
 *
 * Replaces the mix of `toast.success` / `toast.error` / `alert()` /
 * `setError(...)` banners that each screen chose for itself, and the error
 * toasts that dumped a raw exception as their title.
 */

/** Best human-readable line out of whatever the catch block received. */
export function errorMessage(error: unknown): string | undefined {
  if (!error) return undefined;
  if (typeof error === "string") return error || undefined;
  if (error instanceof Error) return error.message || undefined;
  if (typeof error === "object" && "message" in error) {
    const message = (error as { message: unknown }).message;
    if (typeof message === "string") return message || undefined;
  }
  return undefined;
}

/** "Abonnement enregistré", "Article supprimé". Past tense, no trailing period. */
export function notifySuccess(message: string): void {
  toast.success(message);
}

/**
 * "L’abonnement n’a pas été supprimé". Say what failed, not "Erreur".
 * The caught error, when given, becomes the toast description.
 */
export function notifyError(message: string, error?: unknown): void {
  // Prefer the backend's own message over the generic "An API error occurred".
  const description = apiErrorMessage(error, "") || undefined;
  toast.error(message, description ? { description } : undefined);
}

/**
 * Best message out of a failed API call: the response body's `error`,
 * `detail` or `non_field_errors` (nested values are joined), else the
 * error's own message, else `fallback`.
 */
export function apiErrorMessage(error: unknown, fallback: string): string {
  const data =
    typeof error === "object" && error !== null && "response" in error
      ? (error as { response?: { data?: unknown } }).response?.data
      : undefined;
  return formatApiErrorValue(data) || errorMessage(error) || fallback;
}

function formatApiErrorValue(value: unknown): string | null {
  if (typeof value === "string") return value;
  if (Array.isArray(value)) {
    const messages = value
      .map((item) => formatApiErrorValue(item))
      .filter((message): message is string => Boolean(message));
    return messages.length > 0 ? messages.join(" ") : null;
  }
  if (value && typeof value === "object") {
    const valueObject = value as Record<string, unknown>;
    for (const key of ["error", "detail", "non_field_errors"]) {
      const message = formatApiErrorValue(valueObject[key]);
      if (message) return message;
    }
    const messages = Object.values(valueObject)
      .map((item) => formatApiErrorValue(item))
      .filter((message): message is string => Boolean(message));
    return messages.length > 0 ? messages.join(" ") : null;
  }
  return null;
}
